import { Controller, Control, Path, ControllerProps } from "react-hook-form";
import {
  FormControl,
  FormControlProps,
  InputLabel,
  Select,
  SelectProps,
  MenuItem,
  FormHelperText,
} from "@mui/material";

export type FormSelectOption = {
  label: string;
  value: string | number;
};

export type FormSelectProps<T extends Record<string, any>> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  options: FormSelectOption[];
  formControlProps?: Partial<FormControlProps>;
  selectProps?: Partial<SelectProps>;
  controllerProps?: Partial<Omit<ControllerProps<T, Path<T>>, "render">>;
};

const FormSelect = <T extends Record<string, any>>({
  name,
  control,
  label,
  options,
  formControlProps = {},
  selectProps = {},
  controllerProps = {},
}: FormSelectProps<T>) => {
  const isRequired = !!controllerProps.rules?.required;

  return (
    <Controller
      name={name}
      control={control}
      {...controllerProps}
      render={({ field, fieldState }) => (
        <FormControl fullWidth error={!!fieldState.error} {...formControlProps}>
          <InputLabel>{`${label}${isRequired ? " *" : ""}`}</InputLabel>
          <Select
            {...field}
            label={label}
            value={field.value ?? ""}
            {...selectProps}
          >
            {selectProps?.displayEmpty && (
              <MenuItem value="">
                <em>
                  {(selectProps as any).placeholder ?? "Select an option"}
                </em>
              </MenuItem>
            )}

            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {fieldState.error && (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default FormSelect;
